// ═══════════════════════════════════════════════════════════════════════════
// PRATICHE E DOCUMENTI DAL CLIENTE — regole pure (ottobre 2026, piano Pro)
// ═══════════════════════════════════════════════════════════════════════════
// Ogni pratica nasce da un MODELLO (predefinito o dell'azienda) e contiene l'elenco dei
// documenti da raccogliere: dal cliente (si chiedono via email) o dell'impresa (documenti
// tecnici prodotti internamente). Ogni azienda può modificare i modelli e crearne di propri.
// I documenti "riservati" (dati di familiari, dati sanitari) li vede solo chi ha il permesso.
// Fase 2 (non qui): iter della pratica con fasi, date e promemoria.
// ═══════════════════════════════════════════════════════════════════════════

export const ORIGINI = {
  cliente: { id: "cliente", label: "Dal cliente" },
  impresa: { id: "impresa", label: "Dell'impresa" },
};

const D = (id, label, origine = "cliente", extra = {}) => ({ id, label, origine, obbligatorio: true, riservato: false, ...extra });

// ── Fasi dell'iter (fase 2) ────────────────────────────────────────────────
// giorni = tempo previsto oltre il quale la fase è "ferma" (indicativo, modificabile).
// auto: "documenti" → la fase si completa da sola quando i documenti necessari sono tutti ricevuti.
// campi = dati utili della fase; tipo "data" per date, "testo" altrimenti.
const F = (id, label, giorni = null, extra = {}) => ({ id, label, giorni, campi: [], ...extra });
const C = (id, label, tipo = "testo") => ({ id, label, tipo });
export const FASI_PREDEFINITE = {
  connessione_fv: [
    F("documenti", "Raccolta documenti", 15, { auto: "documenti" }),
    F("domanda", "Domanda di connessione inviata", 5, { campi: [C("codice", "Codice della pratica del distributore")] }),
    F("preventivo", "Preventivo ricevuto", 20, { campi: [C("importo", "Importo del preventivo"), C("scadenza_accettazione", "Accettare entro il", "data")] }),
    F("accettazione", "Preventivo accettato e pagato", 10),
    F("lavori", "Lavori completati", 30),
    F("fine_lavori", "Fine lavori comunicata (conformità e regolamento di esercizio)", 7),
    F("connessione", "Impianto connesso e attivato", 20, { campi: [C("data_attivazione", "Data di attivazione", "data")] }),
    F("gse", "Pratica GSE (es. ritiro dedicato)", 30, { facoltativa: true }),
  ],
  detrazioni_enea: [
    F("documenti", "Raccolta documenti", 15, { auto: "documenti" }),
    F("lavori", "Lavori completati", 30, { campi: [C("fine_lavori", "Data di fine lavori", "data")] }),
    F("pagamenti", "Pagamenti verificati", 10),
    F("enea", "Comunicazione ENEA inviata", 80, { campi: [C("protocollo", "Protocollo ENEA")], nota: "Il termine si conta dalla fine dei lavori: verificalo per il tipo di intervento" }),
  ],
  dm37: [
    F("documenti", "Raccolta documenti", 15, { auto: "documenti" }),
    F("impianto", "Impianto completato", 30),
    F("compilata", "Dichiarazione compilata", 7),
    F("consegnata", "Dichiarazione consegnata al committente", 7),
    F("deposito", "Deposito presso l'ente, se previsto", 30, { facoltativa: true }),
  ],
  antimafia: [
    F("documenti", "Raccolta documenti", 15, { auto: "documenti" }),
    F("richiesta", "Richiesta inviata all'ente", 5, { campi: [C("riferimento", "Riferimento della richiesta")] }),
    F("esito", "Esito ricevuto", 30, { campi: [C("valida_fino", "Valida fino al", "data")] }),
  ],
};

export const MODELLI_PREDEFINITI = [
  {
    id: "connessione_fv", label: "Nuova connessione impianto fotovoltaico",
    avviso: "I documenti richiesti variano in base al distributore e alla potenza dell'impianto: verifica l'elenco sul portale del distributore prima di inviare la pratica.",
    documenti: [
      D("id_intestatario", "Documento d'identità e codice fiscale dell'intestatario del contatore (POD)"),
      D("visura_cliente", "Visura camerale e documento del legale rappresentante (se il cliente è un'impresa)", "cliente", { obbligatorio: false }),
      D("bolletta", "Ultima bolletta dell'energia elettrica (POD, potenza disponibile, dati fornitura)"),
      D("titolo_immobile", "Titolo di disponibilità dell'immobile (proprietà, locazione, comodato)"),
      D("consenso_proprietario", "Consenso del proprietario, se diverso dal cliente", "cliente", { obbligatorio: false }),
      D("dati_catastali", "Dati catastali dell'immobile (foglio, particella, subalterno) o visura catastale"),
      D("delega", "Delega / mandato firmato all'impresa o al tecnico per la pratica"),
      D("iban", "IBAN dell'intestatario per gli eventuali contratti con il GSE"),
      D("delibera_condominio", "Delibera condominiale (impianto su parti comuni)", "cliente", { obbligatorio: false }),
      D("titoli_edilizi", "Titoli edilizi o autorizzazioni, se necessari (vincoli, edifici particolari)", "cliente", { obbligatorio: false }),
      D("recapiti", "Recapiti del cliente: email, PEC e telefono"),
      D("schema_unifilare", "Schema unifilare e dati di progetto", "impresa"),
      D("schede_tecniche", "Schede tecniche e certificazioni di moduli, inverter e accumulo (CEI 0-21)", "impresa"),
      D("dico_fv", "Dichiarazione di conformità DM 37/08 (a fine lavori)", "impresa"),
      D("regolamento_esercizio", "Regolamento di esercizio e documenti del distributore", "impresa"),
    ],
  },
  {
    id: "detrazioni_enea", label: "Detrazioni fiscali / comunicazione ENEA",
    avviso: "Requisiti e documenti dipendono dal tipo di intervento e dalle norme in vigore nell'anno: verificali con il consulente fiscale del cliente.",
    documenti: [
      D("id_beneficiario", "Documento d'identità e codice fiscale del beneficiario"),
      D("titolo_immobile_det", "Titolo di disponibilità dell'immobile"),
      D("dati_catastali_det", "Dati catastali dell'immobile"),
      D("pagamenti", "Ricevute dei pagamenti con bonifico \"parlante\""),
      D("fatture", "Fatture dell'intervento", "impresa"),
      D("schede_tecniche_det", "Schede tecniche dei componenti installati", "impresa"),
      D("asseverazione", "Asseverazione del tecnico, se richiesta", "impresa", { obbligatorio: false }),
      D("ricevuta_enea", "Ricevuta della comunicazione ENEA", "impresa"),
    ],
  },
  {
    id: "dm37", label: "Dichiarazione di conformità (DM 37/08)",
    avviso: "Gli allegati obbligatori dipendono dal tipo di impianto e dall'obbligo di progetto.",
    documenti: [
      D("dati_committente", "Dati del committente e dell'immobile"),
      D("progetto", "Progetto o schema dell'impianto", "impresa"),
      D("relazione_materiali", "Relazione con le tipologie dei materiali utilizzati", "impresa"),
      D("schema_realizzato", "Schema dell'impianto realizzato", "impresa"),
      D("visura_impresa", "Visura camerale dell'impresa installatrice (abilitazione)", "impresa"),
      D("conformita_precedenti", "Dichiarazioni di conformità precedenti o parziali, se esistenti", "cliente", { obbligatorio: false }),
      D("dico_firmata", "Dichiarazione di conformità firmata", "impresa"),
    ],
  },
  {
    id: "antimafia", label: "Documentazione antimafia",
    avviso: "I soggetti da verificare dipendono dalla forma giuridica e dalla richiesta dell'ente (D.Lgs. 159/2011). Le dichiarazioni dei familiari sono dati riservati.",
    documenti: [
      D("visura_antimafia", "Visura camerale aggiornata o dichiarazione sostitutiva di iscrizione"),
      D("elenco_soggetti", "Elenco dei soggetti da verificare (amministratori, soci, direttori tecnici, organi di controllo…)"),
      D("familiari_conviventi", "Dichiarazioni sostitutive dei familiari conviventi maggiorenni", "cliente", { riservato: true }),
      D("documenti_dichiaranti", "Documenti d'identità e codici fiscali dei dichiaranti", "cliente", { riservato: true }),
      D("moduli_ente", "Moduli specifici richiesti dall'ente o dal GSE", "cliente", { obbligatorio: false }),
    ],
  },
];

/** Modelli dell'azienda: i predefiniti (con eventuali modifiche) più quelli creati da lei. */
export function modelliAzienda(modifiche = {}, aggiunti = []) {
  const base = MODELLI_PREDEFINITI.map(m => ({ ...m, ...(modifiche[m.id] || {}) }));
  return [...base.filter(m => m.attivo !== false), ...aggiunti.filter(m => m.attivo !== false).map(m => ({ ...m, personalizzato: true }))];
}

export const STATI_VOCE = {
  mancante:     { id: "mancante",     label: "Mancante",       colore: "#dc2626", bg: "#fee2e2", gravita: 3 },
  rifiutato:    { id: "rifiutato",    label: "Da sostituire",  colore: "#b91c1c", bg: "#fef2f2", gravita: 3 },
  daVerificare: { id: "daVerificare", label: "Da verificare",  colore: "#2563eb", bg: "#dbeafe", gravita: 2 },
  valido:       { id: "valido",       label: "Ricevuto",       colore: "#059669", bg: "#d1fae5", gravita: 0 },
  nonNecessario:{ id: "nonNecessario",label: "Non necessario", colore: "#6b7280", bg: "#f3f4f6", gravita: 0 },
  // documento facoltativo non ancora ricevuto: non è un'assenza (non va in rosso)
  facoltativo:  { id: "facoltativo",  label: "Se serve",       colore: "#6b7280", bg: "#f3f4f6", gravita: 0 },
};

/** Stato di una voce della pratica dal suo documento corrente (o dalla scelta "non necessario"). */
export function statoVoce(voce, doc, nonNecessarie = []) {
  if (nonNecessarie.includes(voce.id)) return STATI_VOCE.nonNecessario;
  if (!doc) return voce.obbligatorio ? STATI_VOCE.mancante : STATI_VOCE.facoltativo;
  return STATI_VOCE[doc.stato] || STATI_VOCE.mancante;
}

/**
 * Righe della pratica con lo stato di ogni documento. I facoltativi mai caricati non contano
 * come mancanti nell'avanzamento. Le voci riservate si escludono per chi non può vederle.
 */
export function righePratica(pratica, documenti, { vediRiservati = true } = {}) {
  const correnti = documenti.filter(d => d.praticaId === pratica.id && d.corrente !== false);
  return (pratica.documenti || [])
    .filter(v => vediRiservati || !v.riservato)
    .map(v => {
      const doc = correnti.find(d => d.voceId === v.id) || null;
      return { voce: v, doc, stato: statoVoce(v, doc, pratica.nonNecessarie || []) };
    });
}

/** Avanzamento: documenti necessari ricevuti / documenti necessari. */
export function avanzamento(righe) {
  const necessari = righe.filter(r => !["nonNecessario", "facoltativo"].includes(r.stato.id) && (r.voce.obbligatorio || r.doc));
  const ricevuti = necessari.filter(r => r.stato.id === "valido");
  const percentuale = necessari.length ? Math.round((ricevuti.length / necessari.length) * 100) : 100;
  return { necessari: necessari.length, ricevuti: ricevuti.length, percentuale, completa: necessari.length > 0 && ricevuti.length === necessari.length };
}

/** Documenti da chiedere al cliente ora: obbligatori mancanti o da sostituire, solo "dal cliente". */
export function daChiedereAlCliente(righe) {
  return righe.filter(r => r.voce.origine === "cliente" && ["mancante", "rifiutato"].includes(r.stato.id) && r.voce.obbligatorio);
}

export function testoEmailCliente({ azienda, cliente, pratica, righe, emailRisposte }) {
  const oggetto = `${pratica.titolo || pratica.modelloLabel}: documenti necessari — ${azienda}`;
  const corpo = [
    `Gentile ${cliente || "cliente"},`,
    "",
    `per procedere con la pratica "${pratica.titolo || pratica.modelloLabel}" abbiamo bisogno dei seguenti documenti:`,
    "",
    ...righe.map(r => `• ${r.voce.label}${r.stato.id === "rifiutato" && r.doc?.motivoRifiuto ? ` — da sostituire: ${r.doc.motivoRifiuto}` : ""}`),
    "",
    `Può rispondere a questa email allegando i documenti${emailRisposte ? ` oppure inviarli a ${emailRisposte}` : ""}. Se ha dubbi su qualche documento, ci contatti pure.`,
    "",
    "Grazie,",
    azienda,
    "",
    "—",
    `I documenti sono trattati da ${azienda} solo per la gestione della pratica.`,
  ];
  return { oggetto, corpo: corpo.join("\n") };
}


// ═══════════════════════════════════════════════════════════════════════════
// ITER DELLA PRATICA
// ═══════════════════════════════════════════════════════════════════════════
const p2 = (n) => String(n).padStart(2, "0");
export const oggiIso = (d = new Date()) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const giorniTra = (daIso, aIso) => {
  const a = String(daIso).slice(0, 10).split("-").map(Number), b = String(aIso).slice(0, 10).split("-").map(Number);
  return Math.round((new Date(b[0], b[1] - 1, b[2]) - new Date(a[0], a[1] - 1, a[2])) / 86400000);
};

/** Fasi della pratica: quelle salvate nella pratica, oppure quelle del suo modello; senza le disattivate. */
export function fasiPratica(pratica) {
  const fasi = pratica.fasi || FASI_PREDEFINITE[pratica.modelloId] || [F("documenti", "Raccolta documenti", 15, { auto: "documenti" })];
  return fasi.filter(f => !(pratica.fasiDisattivate || []).includes(f.id));
}

/**
 * Stato dell'iter: per ogni fase lo stato effettivo ("fatta" anche se automatica e completata),
 * la fase attuale (la prima non fatta), da quanti giorni è ferma, se è in ritardo o in
 * escalation (oltre il doppio del previsto), le scadenze inserite entro 7 giorni.
 * inizioIso: data di inizio della pratica (creazione).
 */
export function statoIter(pratica, { documentiCompleti = false, oggi = oggiIso(), inizioIso = oggi } = {}) {
  const iter = pratica.iter || {};
  const fasi = fasiPratica(pratica).map(f => {
    const dati = iter[f.id] || {};
    const fatta = dati.stato === "fatta" || (f.auto === "documenti" && documentiCompleti);
    return { fase: f, dati, stato: fatta ? "fatta" : (dati.stato === "inCorso" ? "inCorso" : "daFare"), dataFatta: fatta ? (dati.dataFatta || "") : "" };
  });
  // Fase attuale = la prima non fatta DOPO l'ultima fase fatta: se la pratica è andata avanti
  // (domanda inviata) mentre manca ancora un documento, la fase attuale è quella più avanzata.
  let ultimaFatta = -1;
  fasi.forEach((r, i) => { if (r.stato === "fatta") ultimaFatta = i; });
  let idx = fasi.findIndex((r, i) => i > ultimaFatta && r.stato !== "fatta" && !r.fase.facoltativa);
  if (idx < 0) idx = fasi.findIndex(r => r.stato !== "fatta" && !r.fase.facoltativa);
  let attuale = null;
  if (idx >= 0) {
    const r = fasi[idx];
    const precedente = fasi.slice(0, idx).reverse().find(x => x.stato === "fatta" && x.dataFatta);
    const avviataIl = r.dati.avviataIl || precedente?.dataFatta || inizioIso;
    const ferma = Math.max(0, giorniTra(avviataIl, oggi));
    attuale = { ...r, avviataIl, giorniFerma: ferma, inRitardo: !!r.fase.giorni && ferma > r.fase.giorni, escalation: !!r.fase.giorni && ferma > 2 * r.fase.giorni };
  }
  const scadenze = [];
  fasi.forEach(r => {
    if (r.stato === "fatta") return;
    const campiData = (r.fase.campi || []).filter(c => c.tipo === "data");
    [...campiData.map(c => ({ label: c.label, data: r.dati.valori?.[c.id] })), { label: "Scadenza", data: r.dati.scadenza }]
      .filter(x => x.data).forEach(x => {
        const g = giorniTra(oggi, x.data);
        if (g <= 7) scadenze.push({ fase: r.fase, label: x.label, data: x.data, giorni: g });
      });
  });
  // fasi rimaste indietro rispetto alla fase attuale (es. documenti ancora mancanti)
  const inSospeso = idx >= 0 ? fasi.slice(0, idx).filter(r => r.stato !== "fatta" && !r.fase.facoltativa).map(r => r.fase) : [];
  const completata = fasi.length > 0 && fasi.every(r => r.stato === "fatta" || r.fase.facoltativa);
  return { fasi, attuale: completata ? null : attuale, scadenze, inSospeso, completata };
}

/** Avvisi della pratica per oggi (ferma, escalation, scadenze). */
export function avvisiPratica(pratica, opzioni) {
  const s = statoIter(pratica, opzioni);
  const avvisi = [];
  if (s.attuale?.inRitardo) avvisi.push({ tipo: s.attuale.escalation ? "escalation" : "ferma", chiave: `${s.attuale.fase.id}|ferma`,
    testo: `ferma da ${s.attuale.giorniFerma} giorni nella fase «${s.attuale.fase.label}» (previsti ${s.attuale.fase.giorni})` });
  s.scadenze.forEach(x => avvisi.push({ tipo: "scadenza", chiave: `${x.fase.id}|${x.label}|${x.data}`,
    testo: `${x.label.toLowerCase()} ${x.data.split("-").reverse().join("/")}${x.giorni < 0 ? " (scaduta)" : x.giorni === 0 ? " (oggi)" : ` (tra ${x.giorni} giorni)`} — fase «${x.fase.label}»` }));
  return { avvisi, stato: s };
}

/** Avvisi da inviare oggi: nuovi, oppure già inviati da almeno 7 giorni (promemoria settimanale). */
export function avvisiDaInviare(avvisi, inviati = {}, oggi = oggiIso()) {
  return avvisi.filter(a => !inviati[a.chiave] || giorniTra(inviati[a.chiave], oggi) >= 7);
}

/** Chi riceve gli avvisi: responsabile, oppure il sostituto se il responsabile è assente; altrimenti nessuno (va all'ufficio). */
export function destinatarioAvvisi(pratica, assenti = new Set()) {
  if (pratica.responsabileUid && !assenti.has(pratica.responsabileUid)) return { uid: pratica.responsabileUid, motivo: "responsabile" };
  if (pratica.sostitutoUid && !assenti.has(pratica.sostitutoUid)) return { uid: pratica.sostitutoUid, motivo: pratica.responsabileUid ? "sostituto (responsabile assente)" : "sostituto" };
  return { uid: null, motivo: "nessun responsabile disponibile" };
}

/** Assenti oggi da assenze registrate e ferie/permessi approvati. */
export function assentiOggi({ assenze = [], ferie = [], oggi = oggiIso() }) {
  const dentro = (dal, al) => dal && dal <= oggi && (!al || oggi <= al);
  const set = new Set();
  assenze.filter(a => a.stato !== "annullata" && dentro(a.dal, a.al)).forEach(a => set.add(a.uid));
  ferie.filter(f => f.stato === "approvata" && dentro(f.dataInizio, f.dataFine)).forEach(f => set.add(f.uid));
  return set;
}

export function testoAvvisoCliente({ azienda, pratica, fase }) {
  const oggetto = `${pratica.titolo || pratica.modelloLabel}: aggiornamento`;
  const corpo = [
    `Gentile ${pratica.clienteNome || "cliente"},`, "",
    `la informiamo che la pratica "${pratica.titolo || pratica.modelloLabel}" ha completato la fase: ${fase.label}.`, "",
    "Le scriveremo appena ci saranno novità. Per qualsiasi domanda può rispondere a questa email.", "",
    "Cordiali saluti,", azienda,
  ];
  return { oggetto, corpo: corpo.join("\n") };
}
